import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import './articleStyles.css';


export interface ArticleMeta {
  slug: string;
  path: string;
  title: string;
  description: string;
  label: string;
  h1: string;
  standfirst: string;
  lastUpdated: string;
  jsonLd: string[];
  isPillar?: boolean;
}

export interface ClusterNavItem { path: string; label: string; }

export interface ClusterConfig {
  /** Base pillar path, e.g. "/commercial-clean-energy-guide" */
  pillarPath: string;
  /** Full display name of the cluster, e.g. "Commercial Clean Energy Guide" */
  clusterName: string;
  /** Short "from Fordje" tagline appended to the intro micro-card */
  fordjeTagline: string;
  /** Left-nav items (in order). First item should point at pillarPath. */
  navItems: ClusterNavItem[];
  /** Section H2 questions per path, for the left-nav dropdowns. */
  sections: Record<string, string[]>;
}

interface ArticleShellProps {
  meta: ArticleMeta;
  cluster?: ClusterConfig;
  children: React.ReactNode;
}

const SITE = 'https://www.fordje.com';

// ---------------------------------------------------------------------------
// Default cluster: Commercial Clean Energy Guide (kept for backwards compat).
// ---------------------------------------------------------------------------
const COMMERCIAL_PILLAR = '/commercial-clean-energy-guide';

const COMMERCIAL_CLUSTER: ClusterConfig = {
  pillarPath: COMMERCIAL_PILLAR,
  clusterName: 'Commercial Clean Energy Guide',
  fordjeTagline: 'AI code and compliance data for commercial and industrial clean-energy projects.',
  navItems: [
    { path: COMMERCIAL_PILLAR, label: 'Overview' },
    { path: `${COMMERCIAL_PILLAR}/expanding-to-a-new-market`, label: 'Expanding to a new market' },
    { path: `${COMMERCIAL_PILLAR}/confirm-jurisdiction-allows-project`, label: 'Confirm jurisdiction allows project' },
    { path: `${COMMERCIAL_PILLAR}/solar`, label: 'Commercial solar' },
    { path: `${COMMERCIAL_PILLAR}/battery-storage`, label: 'Battery storage' },
    { path: `${COMMERCIAL_PILLAR}/ev-charging`, label: 'EV charging' },
    { path: `${COMMERCIAL_PILLAR}/chatgpt-vs-code-research`, label: 'Copilot vs. code research' },
  ],
  sections: {
    [COMMERCIAL_PILLAR]: [
      'What are the requirements for a commercial clean-energy project?',
      'Guides by technology and stage',
      'Why do clean-energy projects get stuck at permitting, interconnection, and inspection?',
    ],
    [`${COMMERCIAL_PILLAR}/expanding-to-a-new-market`]: [
      'What makes a new market so hard to enter?',
      'How to scope a new jurisdiction, step by step',
      'How do I tell if a market is friendly to solar or storage before committing?',
      'Is the state code enough to plan a clean-energy project?',
      'How do teams research a new market for clean-energy expansion?',
    ],
    [`${COMMERCIAL_PILLAR}/confirm-jurisdiction-allows-project`]: [
      "How do I confirm a county allows my project when it's not in the zoning code?",
      "Why can't I just search the code for what I need?",
      "How do I prove a project use isn't allowed when the code doesn't mention it?",
      'Is Copilot or ChatGPT accurate for checking local code and permitting requirements?',
      'How do clean-energy teams research local code requirements today?',
    ],
    [`${COMMERCIAL_PILLAR}/solar`]: [
      'What does it take to permit and build a commercial solar project?',
      'When does a commercial solar project need a conditional use permit?',
      'How is community solar defined versus utility-scale solar?',
      'What are the interconnection steps for a 300 kW commercial PV project?',
      'Why do commercial solar projects get stuck at interconnection and inspection?',
      'How do solar teams research these requirements today?',
    ],
    [`${COMMERCIAL_PILLAR}/battery-storage`]: [
      'Why do battery storage requirements differ from one jurisdiction to the next?',
      'Which jurisdictions follow the IFC versus NFPA 855 for battery storage setbacks?',
      "What do you do if there's no mention of energy storage for a specific county?",
      'Do you consider a battery system a substation or a power plant?',
      "Can a county prohibit storage even if it's not written in the code?",
      'How do teams research battery storage requirements across jurisdictions?',
    ],
    [`${COMMERCIAL_PILLAR}/ev-charging`]: [
      'What does it actually take to permit a commercial EV charging project?',
      'What requirements vary the most by jurisdiction?',
      'How do utility requirements change the project?',
      'Will EV charging cost more on an existing meter or a new service?',
      'How do teams research EV charging permitting and utility requirements?',
    ],
    [`${COMMERCIAL_PILLAR}/chatgpt-vs-code-research`]: [
      'Can I use Copilot or ChatGPT to look up local code requirements?',
      'Why does a general AI tool get jurisdiction questions wrong?',
      "Why isn't manual research or Googling enough on its own?",
      'ChatGPT vs manual research vs FOIA: which is best for code lookup?',
      'What makes a dedicated platform different?',
    ],
  },
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const ArticleShell = ({ meta, cluster = COMMERCIAL_CLUSTER, children }: ArticleShellProps) => {
  const canonical = `${SITE}${meta.path}`;
  const { pathname, hash } = useLocation();
  const [activeHash, setActiveHash] = useState<string>('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => ({ [pathname]: true }));

  useEffect(() => {
    setExpanded((prev) => ({ ...prev, [pathname]: true }));
  }, [pathname]);

  useEffect(() => {
    const container = document.querySelector('.resource-article');
    if (!container) return;
    const headings = Array.from(container.querySelectorAll('h2')) as HTMLHeadingElement[];
    headings.forEach((h) => {
      if (!h.id) h.id = slugify(h.textContent || '');
      h.style.scrollMarginTop = '110px';
    });

    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const onScroll = () => {
      const y = window.scrollY + 140;
      let current = '';
      for (const h of headings) {
        if (h.offsetTop <= y) current = h.id;
      }
      setActiveHash(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname, hash]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://www.fordje.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content="https://www.fordje.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Fordje', item: SITE + '/' },
            { '@type': 'ListItem', position: 2, name: cluster.clusterName, item: SITE + cluster.pillarPath },
            ...(meta.isPillar ? [] : [{ '@type': 'ListItem', position: 3, name: meta.label, item: canonical }]),
          ],
        })}</script>
        {meta.jsonLd.map((block, i) => (
          <script key={i} type="application/ld+json">{block}</script>
        ))}
      </Helmet>

      <Header />

      <main className="flex-1 pt-24 md:pt-28 pb-20">
        <div className="mx-auto px-5 md:px-6 lg:px-8 flex flex-col lg:flex-row lg:gap-10" style={{ maxWidth: '1120px' }}>
          <aside className="lg:w-64 lg:flex-shrink-0 mb-10 lg:mb-0">
            <nav
              aria-label={cluster.clusterName}
              className="lg:sticky lg:top-28 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto border-l border-slate-200"
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-fordje-navy-light pl-4 mb-3">
                {cluster.clusterName}
              </p>
              <ul className="space-y-0.5">
                {cluster.navItems.map((item) => {
                  const active = pathname === item.path;
                  const questions = cluster.sections[item.path] || [];
                  const isOpen = !!expanded[item.path];
                  const hasQuestions = questions.length > 0;
                  return (
                    <li key={item.path}>
                      <div
                        className={`group flex items-stretch -ml-px border-l-2 transition-colors ${
                          active
                            ? 'border-fordje-blue'
                            : 'border-transparent hover:border-fordje-blue/40'
                        }`}
                      >
                        {hasQuestions ? (
                          <button
                            type="button"
                            aria-label={isOpen ? `Collapse ${item.label}` : `Expand ${item.label}`}
                            aria-expanded={isOpen}
                            onClick={() =>
                              setExpanded((prev) => ({ ...prev, [item.path]: !prev[item.path] }))
                            }
                            className="flex items-center justify-center w-6 pl-2 text-fordje-navy-light hover:text-fordje-blue"
                          >
                            <ChevronRight
                              className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                            />
                          </button>
                        ) : (
                          <span className="w-6" />
                        )}
                        <Link
                          to={item.path}
                          className={`flex-1 block text-sm leading-snug py-2 pl-1 pr-2 transition-colors ${
                            active
                              ? 'text-fordje-blue font-semibold'
                              : 'text-fordje-navy-light hover:text-fordje-blue'
                          }`}
                        >
                          {item.label}
                        </Link>
                      </div>
                      {isOpen && hasQuestions && (
                        <ul className="mt-1 mb-2 space-y-0.5">
                          {questions.map((q) => {
                            const id = slugify(q);
                            const isActiveQ = active && activeHash === id;
                            return (
                              <li key={id}>
                                <a
                                  href={active ? `#${id}` : `${item.path}#${id}`}
                                  className={`block text-[13px] leading-snug py-1.5 pl-9 -ml-px border-l-2 transition-colors ${
                                    isActiveQ
                                      ? 'border-fordje-blue text-fordje-blue font-medium'
                                      : 'border-transparent text-fordje-navy-light/80 hover:text-fordje-blue hover:border-fordje-blue/40'
                                  }`}
                                >
                                  {q}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          <article className="flex-1 min-w-0" style={{ maxWidth: '760px' }}>
            <nav aria-label="Breadcrumb" className="mb-5 text-sm">
              <ol className="flex flex-wrap items-center gap-1.5 text-fordje-navy-light">
                <li>
                  <Link to="/" className="hover:text-fordje-blue hover:underline">Fordje</Link>
                </li>
                <li aria-hidden="true" className="text-fordje-navy-light/50">/</li>
                {meta.isPillar ? (
                  <li aria-current="page" className="text-fordje-navy-dark font-medium">
                    {cluster.clusterName}
                  </li>
                ) : (
                  <>
                    <li>
                      <Link to={cluster.pillarPath} className="hover:text-fordje-blue hover:underline">
                        {cluster.clusterName}
                      </Link>
                    </li>
                    <li aria-hidden="true" className="text-fordje-navy-light/50">/</li>
                    <li aria-current="page" className="text-fordje-navy-dark font-medium truncate max-w-[280px]">
                      {meta.label}
                    </li>
                  </>
                )}
              </ol>
            </nav>

            <p className="text-xs font-semibold uppercase tracking-widest text-fordje-blue mb-4">
              {meta.label}
            </p>


            <h1 className="font-ubuntu text-3xl md:text-[2.15rem] leading-[1.2] font-bold text-fordje-navy-dark mb-3">
              {meta.h1}
            </h1>

            <p className="text-lg md:text-[1.18rem] text-fordje-navy-light leading-relaxed mb-3">
              {meta.standfirst}
            </p>

            <p className="text-sm text-fordje-gray-dark mb-6">
              Last updated: {meta.lastUpdated}
            </p>

            <div className="flex items-start gap-3 p-3.5 mb-10 rounded-lg border border-slate-200 bg-fordje-gray-light">
              <img
                src="/lovable-uploads/595a50f5-c631-4403-8464-cad23f811cfe.png"
                alt="Fordje"
                className="h-8 w-8 rounded"
              />
              <div>
                <p className="text-sm text-fordje-navy-dark leading-snug">
                  <span className="font-semibold">From Fordje</span> — {cluster.fordjeTagline}
                </p>
                <p className="text-xs text-fordje-navy-light mt-1">
                  Part of the{' '}
                  <Link
                    to={cluster.pillarPath}
                    className="text-fordje-blue hover:text-fordje-blue-dark hover:underline font-medium"
                  >
                    {cluster.clusterName}
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="resource-article text-fordje-navy-dark">
              {children}
            </div>

            {!meta.isPillar && (
              <div className="mt-16 pt-8 border-t border-slate-200 text-sm">
                <Link
                  to={cluster.pillarPath}
                  className="text-fordje-blue hover:text-fordje-blue-dark hover:underline font-medium"
                >
                  ← Back to: {cluster.clusterName}
                </Link>
              </div>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ArticleShell;
